import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import FormInput from '../../../components/form/FormInput';
import FormToggle from '../../../components/form/FormToggle';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import {
  supervisorRoleAdd,
  supervisorRoleGetById,
  supervisorRoleUpdate
} from '../../../store/slices/tenantSlices/supervisorSlice';

const AddEditCompanyRoleModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();
  const filteredUserRolePermission = useMemo(() => {
    return userRolePermission?.filter((module) =>
      Object.values(module?.permission || {}).some((v) => v === true)
    );
  }, [userRolePermission]);

  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const initialValues = {
    name: '',
    status: 'true',
    permissions: []
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required')
  });

  const onSubmit = async () => {
    const payload = { ...values, permissions: selectedPermissions };

    if (isEdit) {
      try {
        payload['id'] = editRecord?.id;
        const response = await dispatch(supervisorRoleUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const response = await dispatch(supervisorRoleAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchSupervisorRoleById = async () => {
    try {
      const response = await dispatch(supervisorRoleGetById(editRecord?.id)).unwrap();
      if (response && response?.success) {
        setValues({
          name: response?.role?.name,
          status: String(response?.role?.status)
        });

        const rolePermissions = [...(response?.permissions || [])]?.map((val) => {
          const moduleSource = userRolePermission?.find(
            (m) => m.module_permission_id === val?.module_permission_id
          );

          const cleanedPermission = {};
          if (moduleSource) {
            Object.keys(moduleSource.permission).forEach((key) => {
              if (moduleSource.permission[key]) {
                cleanedPermission[key] = !!val.permission[key];
              } else {
                cleanedPermission[key] = false;
              }
            });
          }

          return {
            module_permission_id: val?.module_permission_id,
            permission: cleanedPermission
          };
        });
        setSelectedPermissions(rolePermissions || []);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handlePermissionChange = (moduleId, permissionKey, checked) => {
    const moduleSource = userRolePermission?.find((m) => m.module_permission_id === moduleId);
    if (!moduleSource || !moduleSource.permission[permissionKey]) {
      console.warn(
        `Attempted to change unauthorized permission: ${permissionKey} for module ${moduleId}`
      );
      return;
    }

    setSelectedPermissions((prev) => {
      const updated = JSON.parse(JSON.stringify(prev)); // Deep clone to avoid mutation
      const existingModule = updated.find((item) => item.module_permission_id === moduleId);

      if (existingModule) {
        existingModule.permission[permissionKey] = checked;
      } else {
        updated.push({
          module_permission_id: moduleId,
          permission: { [permissionKey]: checked }
        });
      }
      return updated;
    });
  };

  useEffect(() => {
    if (isOpen && !isEdit) {
      const allPermissions = filteredUserRolePermission?.map((module) => {
        const defaultPerms = {};
        Object.keys(module.permission).forEach((key) => {
          defaultPerms[key] = !!module.permission[key]; // Default to source value
        });
        return {
          module_permission_id: module.module_permission_id,
          permission: defaultPerms
        };
      });
      setSelectedPermissions(allPermissions || []);
    }
  }, [isOpen, isEdit, filteredUserRolePermission]);

  useEffect(() => {
    if (isEdit && editRecord) {
      fetchSupervisorRoleById();
    }
  }, [editRecord, isEdit]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Supervisor Role' : 'Add Supervisor Role'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="supervisor-role" onSubmit={handleSubmit}>
          <Row>
            <Col lg="6" md="12">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="6" md="12">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
          </Row>

          <div className="w-100 py-3 px-4 mb-3 bg-primary text-white fw-bold rounded">
            Role Permissions
          </div>

          <div className="module-permissions">
            {filteredUserRolePermission?.map((module) => (
              <Row
                key={module.module_permission_id}
                className="module-card mb-3 p-3 border rounded mx-1">
                <Col lg="4" className="module-name">
                  <strong>{module.module_name}</strong>
                </Col>
                <Col lg="8">
                  <Row>
                    {Object.keys(module.permission)
                      .filter((permission) => module.permission[permission]) // Only show active permissions
                      .map((permissionKey) => (
                        <Col key={permissionKey} lg="2" sm="6">
                          <div className="d-flex flex-row">
                            <div className="me-1">
                              <FormGroup check>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    id="check_header"
                                    checked={selectedPermissions.some(
                                      (perm) =>
                                        perm.module_permission_id === module.module_permission_id &&
                                        perm.permission[permissionKey]
                                    )}
                                    onChange={(e) =>
                                      handlePermissionChange(
                                        module.module_permission_id,
                                        permissionKey,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </Label>
                              </FormGroup>
                            </div>
                            <div className="text-primary fw-bold">
                              {permissionKey.replace('is_', '').replace('_', ' ').toUpperCase()}
                            </div>
                          </div>
                        </Col>
                      ))}
                  </Row>
                </Col>
              </Row>
            ))}
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="supervisor-role" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditCompanyRoleModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditCompanyRoleModal;
